import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Select, { TOption } from "@components/Select";
import { toast } from "react-toastify";
import { useLang } from "@src/hooks/useLang";
import { useFarmsVM } from "@stores/FarmStore";
import { observer } from "mobx-react-lite";
import {
  getJohnDeereStatus,
  getJohnDeereOrganizations,
  getJohnDeereClients,
  getJohnDeereFarms,
  getJohnDeereFields,
  getJohnDeereFieldsByOrg,
  importJohnDeereFields,
  JohnDeereOrganization,
  JohnDeereClient,
  JohnDeereFarm,
  JohnDeereField,
  safeDeereUrl,
} from "@services/johnDeereService";
import Loading from "@components/Loading";
import Checkbox from "@components/Checkbox";
import SearchInput from "@components/SearchInput";
import { ReactComponent as SearchIcon } from "@assets/icons/search.svg";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/** Client-selector sentinel: import every field in the organization (all growers). */
const ALL_CLIENTS = "__all__";

const ImportFromJohnDeereDialog: React.FC<IProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const lang = useLang();
  const jdLang = lang.johnDeere;
  const farmVM = useFarmsVM();

  const [statusLoading, setStatusLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [organizations, setOrganizations] = useState<JohnDeereOrganization[]>([]);
  const [clients, setClients] = useState<JohnDeereClient[]>([]);
  const [farms, setFarms] = useState<JohnDeereFarm[]>([]);
  const [fields, setFields] = useState<JohnDeereField[]>([]);

  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [selectedFarm, setSelectedFarm] = useState<string>("");
  const [selectedFieldUris, setSelectedFieldUris] = useState<Set<string>>(new Set());
  const [fieldQuery, setFieldQuery] = useState("");

  const [orgsLoading, setOrgsLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [connectionRequired, setConnectionRequired] = useState(false);
  const [connectionsUrl, setConnectionsUrl] = useState<string | null>(null);
  /**
   * "client" → org exposes growers, use Org→Client→Fields.
   * "farm"   → no clients (or clients denied), fall back to Org→Farm→Fields.
   */
  const [mode, setMode] = useState<"client" | "farm">("farm");
  /** When farms 403 in farm mode, skip the farm step and load fields directly from org. */
  const [farmsBypass, setFarmsBypass] = useState(false);

  const selectedOrgData = organizations.find((o) => o.selfUri === selectedOrg);
  const selectedFarmData = farms.find((f) => f.selfUri === selectedFarm);
  const selectedClientData = clients.find((cl) => cl.id === selectedClient);

  /** Shared John Deere error → toast (terms / connections / generic). */
  const showJdError = (e: any, fallback: string) => {
    const msg = e?.message ?? fallback;
    // Only https links on John Deere's domain reach an href (safeDeereUrl).
    const termsUrl = safeDeereUrl(e?.termsLocation);
    const connUrl = safeDeereUrl(e?.connectionsUrl);
    if (termsUrl) {
      toast.error(
        <span>
          {msg}{" "}
          <a href={termsUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
            Accept terms
          </a>
        </span>,
        { autoClose: false }
      );
    } else if (connUrl) {
      toast.error(
        <span>
          Access denied. If already connected, check <strong>Locations</strong> permission in{" "}
          <a href="https://teammanager.deere.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
            Team Manager
          </a>
          . Otherwise, grant access at{" "}
          <a href={connUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
            connections.deere.com
          </a>
        </span>,
        { autoClose: false }
      );
      setConnectionsUrl(connUrl);
      setConnectionRequired(true);
    } else {
      toast.error(msg);
    }
  };

  const resetDownstream = () => {
    setClients([]);
    setFarms([]);
    setFields([]);
    setSelectedClient("");
    setSelectedFarm("");
    setSelectedFieldUris(new Set());
    setFieldQuery("");
    setFarmsBypass(false);
  };

  useEffect(() => {
    if (!visible) return;
    setStatusLoading(true);
    getJohnDeereStatus()
      .then((s) => setConnected(s.connected))
      .catch(() => setConnected(false))
      .finally(() => setStatusLoading(false));
  }, [visible]);

  useEffect(() => {
    if (!visible || !connected) return;
    setOrgsLoading(true);
    getJohnDeereOrganizations()
      .then((r) => {
        setOrganizations(r.organizations ?? []);
        setConnectionRequired(r.connectionRequired ?? false);
        setConnectionsUrl(safeDeereUrl(r.connectionsUrl));
        setSelectedOrg("");
        resetDownstream();
      })
      .catch((e: any) => showJdError(e, "Failed to load organizations"))
      .finally(() => setOrgsLoading(false));
  }, [visible, connected]);

  /** Load fields filtered to a farm (farm mode). */
  const loadFieldsForFarm = (farmUri: string) => {
    setFieldsLoading(true);
    getJohnDeereFields(farmUri)
      .then((r) => {
        setFields(r.fields ?? []);
        setSelectedFieldUris(new Set());
        setFieldQuery("");
      })
      .catch((e: any) => showJdError(e, "Failed to load fields"))
      .finally(() => setFieldsLoading(false));
  };

  /** Load fields for the org, optionally filtered to a client (grower). */
  const loadFieldsForOrg = (orgUri: string, clientId?: string) => {
    setFieldsLoading(true);
    getJohnDeereFieldsByOrg(orgUri, clientId)
      .then((r) => {
        setFields(r.fields ?? []);
        setSelectedFieldUris(new Set());
        setFieldQuery("");
      })
      .catch((e: any) => showJdError(e, "Failed to load fields"))
      .finally(() => setFieldsLoading(false));
  };

  /** Farm-mode loader (also used as the fallback when an org has no clients). */
  const loadFarms = (orgUri: string) => {
    setMode("farm");
    setFarmsLoading(true);
    setFarmsBypass(false);
    getJohnDeereFarms(orgUri)
      .then((r) => {
        setFarms(r.farms ?? []);
        setSelectedFarm("");
        setFields([]);
        setSelectedFieldUris(new Set());
        setFieldQuery("");
      })
      .catch((e: any) => {
        const status = e?.statusCode ?? e?.status;
        // Farms 403 → bypass farms and load fields directly from org.
        if (status === 403 || (e?.message && e.message.includes("403"))) {
          console.log("[John Deere] Farms 403 — bypassing farms, loading fields from org directly");
          setFarmsBypass(true);
          setFarms([]);
          setSelectedFarm("");
          loadFieldsForOrg(orgUri);
          return;
        }
        showJdError(e, "Failed to load farms");
      })
      .finally(() => setFarmsLoading(false));
  };

  // Org change → load clients first; fall back to farms when the org has none.
  useEffect(() => {
    if (!selectedOrg || !connected) {
      resetDownstream();
      return;
    }
    resetDownstream();
    setClientsLoading(true);
    getJohnDeereClients(selectedOrg)
      .then((r) => {
        const cl = r.clients ?? [];
        if (cl.length > 0) {
          setClients(cl);
          setMode("client");
        } else {
          // Org has no clients — use the legacy farm flow.
          loadFarms(selectedOrg);
        }
      })
      .catch(() => {
        // Clients unavailable (older org / permissions) — fall back to farms.
        loadFarms(selectedOrg);
      })
      .finally(() => setClientsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, connected]);

  // Client change (client mode) → load that grower's fields.
  useEffect(() => {
    if (mode !== "client" || !selectedClient || !connected) return;
    loadFieldsForOrg(
      selectedOrg,
      selectedClient === ALL_CLIENTS ? undefined : selectedClient
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, mode, connected]);

  // Farm change (farm mode) → load that farm's fields.
  useEffect(() => {
    if (mode !== "farm") return;
    if (!selectedFarm || !connected) {
      // Don't clear fields when farmsBypass is active (already loaded from org).
      if (!farmsBypass) {
        setFields([]);
        setSelectedFieldUris(new Set());
        setFieldQuery("");
      }
      return;
    }
    loadFieldsForFarm(selectedFarm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFarm, mode, connected, farmsBypass]);

  /** Fields shown in the list: name-filtered, always sorted alphabetically. */
  const visibleFields = useMemo(() => {
    const q = fieldQuery.trim().toLowerCase();
    return fields
      .filter((f) => !q || f.name.toLowerCase().includes(q))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [fields, fieldQuery]);

  const allVisibleSelected =
    visibleFields.length > 0 &&
    visibleFields.every((f) => selectedFieldUris.has(f.selfUri));

  const handleToggleField = (selfUri: string) => {
    setSelectedFieldUris((prev) => {
      const next = new Set(prev);
      if (next.has(selfUri)) next.delete(selfUri);
      else next.add(selfUri);
      return next;
    });
  };

  const handleSelectAllFields = () => {
    setSelectedFieldUris((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleFields.forEach((f) => next.delete(f.selfUri));
      } else {
        visibleFields.forEach((f) => next.add(f.selfUri));
      }
      return next;
    });
  };

  const handleImport = async () => {
    if (!selectedOrgData || selectedFieldUris.size === 0) return;
    if (mode === "farm" && !farmsBypass && !selectedFarmData) return;
    if (mode === "client" && !selectedClient) return;
    setImportLoading(true);
    try {
      const useClient = mode === "client" && selectedClient !== ALL_CLIENTS;
      const result = await importJohnDeereFields({
        orgUri: selectedOrgData.selfUri,
        farmUri: mode === "farm" ? selectedFarmData?.selfUri : undefined,
        fieldUris: Array.from(selectedFieldUris),
        clientId: useClient ? selectedClient : undefined,
        clientName: useClient ? selectedClientData?.name : undefined,
        farmId: farmVM.currentFarmId,
      });
      const count = result.imported?.length ?? 0;
      if (count > 0) {
        toast.success(jdLang.importSuccess);
        onSuccess();
        onClose();
      }
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach((err) => toast.error(err));
      }
    } catch (e: any) {
      showJdError(e, "Import failed");
    } finally {
      setImportLoading(false);
    }
  };

  const orgOptions: TOption[] = organizations.map((o) => ({
    value: o.selfUri,
    title: o.name,
  }));

  const clientOptions: TOption[] = [
    { value: ALL_CLIENTS, title: jdLang.allClients },
    ...clients.map((cl) => ({ value: cl.id, title: cl.name })),
  ];

  const farmOptions: TOption[] = farms.map((f) => ({
    value: f.selfUri,
    title: f.name,
  }));

  const showFields =
    (mode === "client" && !!selectedClient) ||
    (mode === "farm" && (!!selectedFarm || farmsBypass));

  const canImport =
    !!selectedOrg &&
    (mode === "client" ? !!selectedClient : farmsBypass || !!selectedFarm) &&
    selectedFieldUris.size > 0 &&
    !importLoading;

  const notConnectedMessage =
    jdLang.connectToImport;

  return (
    <Dialog
      title={jdLang.importTitle}
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 500 }}
    >
      {statusLoading ? (
        <Loading />
      ) : !connected ? (
        <Column crossAxisSize="max">
          <Text size={14} color="#6B7280">
            {notConnectedMessage}
          </Text>
          <SizedBox height={16} />
          <Row justifyContent="flex-end">
            <Button onClick={onClose} kind="outline">
              {jdLang.close}
            </Button>
          </Row>
        </Column>
      ) : (
        <Column crossAxisSize="max" style={{ gap: 16 }}>
          {connectionRequired && connectionsUrl && (
            <div
              style={{
                background: "#EFF6FF",
                border: "1px solid #3B82F6",
                borderRadius: 8,
                padding: 12,
              }}
            >
              <Text size={13} color="#1E40AF" style={{ display: "block", marginBottom: 6 }}>
                Grant <strong>Organization</strong> and <strong>Locations</strong> to import fields.
              </Text>
              <Text size={12} color="#4B5563" style={{ display: "block", marginBottom: 10 }}>
                At connections.deere.com, expand each category and enable <strong>Organization</strong> (View, Analyze, Manage). Locations alone is not enough.
              </Text>
              <a
                href={connectionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 600, textDecoration: "underline", color: "#1D4ED8" }}
              >
                Grant permissions at connections.deere.com →
              </a>
            </div>
          )}
          <div>
            <Text size={14} style={{ marginBottom: 4, fontWeight: 600 }}>
              {jdLang.organization}
            </Text>
            <Select
              value={selectedOrg}
              onChange={setSelectedOrg}
              options={orgsLoading ? [{ value: "", title: "Loading..." }] : orgOptions}
              renderValue={(opt) => opt?.title || jdLang.selectOrg}
            />
          </div>

          {mode === "client" && (
            <div>
              <Text size={14} style={{ marginBottom: 4, fontWeight: 600 }}>
                {jdLang.client}
              </Text>
              <Select
                value={selectedClient}
                onChange={setSelectedClient}
                options={
                  clientsLoading
                    ? [{ value: "", title: "Loading..." }]
                    : !selectedOrg
                    ? []
                    : clientOptions
                }
                renderValue={(opt) => opt?.title || jdLang.selectClient}
              />
            </div>
          )}

          {mode === "farm" && !farmsBypass && (
            <div>
              <Text size={14} style={{ marginBottom: 4, fontWeight: 600 }}>
                {jdLang.farm}
              </Text>
              <Select
                value={selectedFarm}
                onChange={setSelectedFarm}
                options={
                  farmsLoading
                    ? [{ value: "", title: "Loading..." }]
                    : !selectedOrg
                    ? []
                    : farmOptions
                }
                renderValue={(opt) =>
                  opt?.title || jdLang.selectFarm
                }
              />
            </div>
          )}

          {showFields && (
            <div>
              <Row justifyContent="space-between" alignItems="center" style={{ marginBottom: 8 }}>
                <Text size={14} style={{ fontWeight: 600 }}>
                  {jdLang.fields}
                </Text>
                <Button
                  kind="outline"
                  onClick={handleSelectAllFields}
                  style={{ padding: "4px 12px", maxWidth: "fit-content" }}
                >
                  {fieldsLoading
                    ? "Loading..."
                    : allVisibleSelected
                    ? jdLang.deselectAll
                    : jdLang.selectAll}
                </Button>
              </Row>
              {!fieldsLoading && fields.length > 5 && (
                <SearchInput
                  style={{ width: "100%", marginBottom: 8 }}
                  value={fieldQuery}
                  onChange={(e) => setFieldQuery(e.target.value)}
                  placeholder={jdLang.searchFields}
                  icon={<SearchIcon />}
                />
              )}
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                {fieldsLoading ? (
                  <Loading />
                ) : visibleFields.length === 0 ? (
                  <Text size={14} color="#6B7280">
                    {jdLang.noFields}
                  </Text>
                ) : (
                  visibleFields.map((f) => (
                    <div
                      key={f.selfUri}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 0",
                        cursor: "pointer",
                      }}
                      onClick={() => handleToggleField(f.selfUri)}
                    >
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedFieldUris.has(f.selfUri)}
                          onChange={() => handleToggleField(f.selfUri)}
                        />
                      </span>
                      <Text size={14}>
                        {f.name}
                        {f.area != null ? ` (${f.area} ha)` : ""}
                      </Text>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button onClick={onClose} kind="outline">
              {jdLang.cancel}
            </Button>
            <Button onClick={handleImport} disabled={!canImport}>
              {importLoading ? <Loading /> : jdLang.importButton}
            </Button>
          </Row>
        </Column>
      )}
    </Dialog>
  );
};

export default observer(ImportFromJohnDeereDialog);
